import { Box, Button } from '@chakra-ui/react';
import { useEffect } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useGetOneUserResource, useUpdateOneUserResource } from 'shared/api/requests/user-resources';
import Card from 'shared/components/horizon-ui/card/Card';
import { HSeparator } from 'shared/components/horizon-ui/separator/Separator';
import Link from 'shared/components/Link';
import Loader from 'shared/components/Loader';
import {
  getUpdateUserResourceValues,
  UpdateUserResourceFormT,
  updateUserResourceResolver
} from 'shared/form-values/user-resources';
import useGetColors from 'shared/hooks/useGetColors';
import { PathsE, PathsParams } from 'shared/routes';
import { fillFormErrors } from 'shared/utils/devUtils';
import PageTitle from '../admin/PageTitle';
import UserResourceForm from './Form';

const Update = () => {
  const { t } = useTranslation();
  const { resourceId } = useParams<PathsParams[PathsE.UPDATE_USER_RESOURCE]>();
  const { mutate, isPending: isLoadingUpdate } = useUpdateOneUserResource({
    pathParams: { resourceId },
    params: undefined,
  });
  const { data, isLoading } = useGetOneUserResource({
    pathParams: { resourceId },
    params: undefined,
  });

  const { gray800, white, gray100, brand500, brand400 } = useGetColors();
  
  const methods = useForm<UpdateUserResourceFormT>({
    defaultValues: getUpdateUserResourceValues(data),
    resolver: updateUserResourceResolver,
    mode: "onChange",
  });
  const { formState: { isValid }, trigger } = methods;

  useEffect(() => {
    trigger();
  }, []);
  
  useEffect(() => {
    if (!data) return;
    methods.reset({
      resourceId: data.resource?.id,
      resourceType: data.resource?.type,
      userId: data.user?.id,
    });
  }, [data])

  const onSubmit: SubmitHandler<UpdateUserResourceFormT> = (values) => {
    mutate(values, { onError: (error) => fillFormErrors(methods.setError, error) })
  };

  const breadcrumbs = [
    {
      path: PathsE.HOME,
      text: t('general.home'),
    },
    {
      path: PathsE.USER_RESOURCES,
      text: t('users.users'),
    },
    {
      path: PathsE.SHOW_USER_RESOURCE,
      text: t('users.viewUserResource'),
      pathsParams: {
        resourceId: data?.id,
      },
    },
  ];

  if (isLoading) return <Loader />;

  return (
    <Box>
      <PageTitle
        title={t('users.editUserResource')}
        breadcrumbs={breadcrumbs}
        backUrl={{
          path: PathsE.SHOW_USER_RESOURCE,
          pathsParams: {
            resourceId: data?.id,
          }
        }}
      />
      <Card mb="20px" borderRadius="20px" px="30px">
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <UserResourceForm />
            <HSeparator my="20px" mx="-20px" width="calc(100% + 40px)" />
            <Link<PathsParams[PathsE.SHOW_USER_RESOURCE]> path={PathsE.SHOW_USER_RESOURCE} pathsParams={{ resourceId }}>
              <Button
                color={gray800}
                bgColor={gray100}
                borderRadius="8px"
                w="100px"
              >
                {t('buttons.cancel')}
              </Button>
            </Link>
            <Button
              color={white}
              _hover={{
                bgColor: brand400,
              }}
              bgColor={brand500}
              borderRadius="8px"
              ml="20px"
              isLoading={isLoadingUpdate}
              w="100px"
              type="submit"
              isDisabled={!isValid}
            >
              {t('buttons.edit')}
            </Button>
          </form>
        </FormProvider>
      </Card>
    </Box>
  );
};

export default Update;
